/**
 * Pending changes come from the server and only from the server.
 *
 * There is no local copy and no optimistic card: after a reload the panel shows what the
 * server says is still waiting, which is the same thing it showed before the reload only if
 * the server agrees. A confirmed change leaves no card behind because the row is no longer
 * pending.
 */
import { api } from '@/api/client';
import type { SettingEdit } from './SettingEdit';

/** One approved absence to change or withdraw. The day counts are the API's own preview. */
export interface AbsenceChangeEntry {
  employee: string | null;
  type?: string | null;
  type_before?: string | null;
  from: string;
  to: string;
  from_before?: string;
  to_before?: string;
  deductible: boolean;
  days_before?: number;
  days_after?: number;
  days_back?: number;
  length_days?: number;
  length_unit?: string;
  warnings?: string[];
  limits_unknown?: boolean;
}

/** A proposal, in the shapes the panel draws. */
export type ActionCard =
  | {
      action_id: string;
      kind: 'move';
      shift_id: number;
      employee: string | null;
      date: string;
      weekday: string;
      from: { date: string | null; start: string; end: string };
      to: { date: string; start: string; end: string };
      moves_day: boolean;
    }
  | {
      action_id: string;
      kind: 'absence_create';
      entries: {
        employee: string | null;
        type?: string;
        from: string;
        to: string;
        days?: number;
        deductible: boolean;
        length_days?: number;
        length_unit?: string;
        warnings?: string[];
        limits_unknown?: boolean;
      }[];
    }
  | {
      action_id: string;
      kind: 'absence_decision';
      entries: {
        employee: string | null;
        type?: string;
        from: string;
        to: string;
        decision: 'approve' | 'reject';
      }[];
    }
  | { action_id: string; kind: 'absence_update'; entries: AbsenceChangeEntry[] }
  | { action_id: string; kind: 'absence_withdraw'; entries: AbsenceChangeEntry[] }
  | {
      action_id: string;
      kind: 'create' | 'publish';
      entries: {
        employee: string | null;
        /** the person, on the list the browser reads (create); the Vaktplan places by it */
        user_id?: number | null;
        /** the shift a publish entry is about */
        shift_id?: number;
        date: string;
        weekday: string;
        start: string;
        end: string;
        crosses_midnight: boolean;
      }[];
    }
  | {
      /** a change through the API itself (owner, 2026-09-25): the server writes the title and lines */
      action_id: string;
      kind: 'api_call';
      title: string;
      button: string;
      method: string;
      path: string;
      lines: { field: string; before?: string; after: string }[];
      /** a setting card's values and what it held, for editing it before Utfør and the Undo after (#154 HUG-06) */
      edit?: SettingEdit;
    }
  | {
      /** the daily-operations cards (map #5): the server writes the title, lines, note and button */
      action_id: string;
      kind: 'shift_ops';
      op: 'approve' | 'punch' | 'free' | 'delete' | 'requests' | 'copy' | 'template';
      title: string;
      note: string;
      button: string;
      entries: {
        employee: string | null;
        date?: string;
        start?: string;
        end?: string;
        detail?: string;
        decision?: 'approve' | 'reject';
      }[];
    };

export const loadPending = () => api<{ pending: ActionCard[] }>('/poc/actions').then((r) => r.pending);

export const confirmAction = (id: string) =>
  api<{ applied: boolean }>(`/poc/actions/${id}/confirm`, { method: 'POST' });

/** A waiting setting card's values changed before Utfør; the server keeps its guard (#154 HUG-06). */
export const editAction = (id: string, body: Record<string, unknown>) =>
  api<ActionCard>(`/poc/actions/${id}`, { method: 'PUT', body: JSON.stringify({ body }) });

export const cancelAction = (id: string) =>
  api<{ cancelled: boolean }>(`/poc/actions/${id}/cancel`, { method: 'POST' });

/** The transcript as the server holds it. What the panel shows after a reload is this. */
export const loadThread = () => api<{ messages: StoredMessage[] }>('/poc/thread').then((r) => r.messages);

export const clearThread = () => api<{ cleared: boolean }>('/poc/thread', { method: 'DELETE' });

export interface StoredMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  parts: { type: string; text?: string }[];
  metadata?: { source?: string };
}

/** "Rapporter et problem" (map #5): what would be sent, then the send with the lines taken out. */
export interface ReportItem {
  id: string;
  who: string;
  text: string;
}
export const loadReport = () =>
  api<{ items: ReportItem[]; cards: { id: string; text: string }[] }>('/poc/report');
/** What is sent is exactly the ids kept: a line saved after the preview was never shown, so it never goes. */
export const sendReport = (description: string, kept: string[], keptCards: string[]) =>
  api<{ sent: boolean; stored: boolean; message: string }>('/poc/report', {
    method: 'POST',
    body: JSON.stringify({ description, kept, kept_cards: keptCards }),
  });

/** "3 ting trenger deg i dag" (map #5): computed on the server, each with the question it asks. */
export interface BriefingItem {
  key: string;
  text: string;
  ask: string;
}
export const loadBriefing = () => api<{ items: BriefingItem[] }>('/poc/briefing').then((r) => r.items);
